import { useAuthentication } from '@ravago/shared/authentication';
import { CountryUtils, CustomerUtils, ProductUtils } from '@ravago/shared/next-data-access/client';
import { ProductDetail } from '@ravago/shared/page-data/models/elements';

import { useQuery } from '@tanstack/react-query';

interface Props {
   productId: string;
   defaultProduct?: ProductDetail;
   isCustomerProduct: boolean;
}

export const useRequestForQuotationState = ({ productId, defaultProduct, isCustomerProduct }: Props) => {
   const { accessToken } = useAuthentication();

   const productDetailQuery = useQuery({
      queryKey: ['product-details', productId],
      queryFn: () => ProductUtils.getProductDetails(productId),
      initialData: defaultProduct,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      enabled: !isCustomerProduct
   });

   const colorsQuery = useQuery({
      queryKey: ['customer-products', productId, 'colors'],
      queryFn: () => ProductUtils.getCustomerProductColors(productId),
      enabled: isCustomerProduct
   });

   const packagingGroupsQuery = useQuery({
      queryKey: ['customer-products', productId, 'packaging-groups'],
      queryFn: () => ProductUtils.getCustomerProductPackagingGroups(productId),
      enabled: isCustomerProduct
   });

   const customerQuery = useQuery({
      queryKey: ['current-customer'],
      queryFn: () => CustomerUtils.getCurrentCustomer(),
      enabled: !!accessToken
   });

   const countriesQuery = useQuery({
      queryKey: ['country-locales'],
      queryFn: () => CountryUtils.getCountries()
   });

   const product = defaultProduct || productDetailQuery.data;
   const colors = isCustomerProduct ? colorsQuery.data : product?.colors;
   const packagingGroups = isCustomerProduct ? packagingGroupsQuery.data : product?.packagingGroups;

   const loading =
      (isCustomerProduct ? colorsQuery.isLoading || packagingGroupsQuery.isLoading : productDetailQuery.isLoading) ||
      customerQuery.isLoading ||
      countriesQuery.isLoading;

   return {
      colors,
      packagingGroups,
      customer: customerQuery.data,
      countries: countriesQuery.data,
      loading
   };
};
